import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Lock } from "lucide-react";
import RentifyLogo from "./RentifyLogo";

const PaymentBadge = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center justify-center w-12 h-8 bg-background rounded border border-border/50">
    {children}
  </div>
);

const Footer = () => {
  const { t } = useLanguage();
  
  return (
    <footer className="bg-background border-t border-border/50 relative z-10">
      <div className="container px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="mb-4">
              <RentifyLogo size="lg" linkToHome={true} />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your community rental marketplace for everyday items.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4 text-foreground">{t("quickLinks")}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/browse" className="text-muted-foreground hover:text-foreground transition-colors">{t("browse")}</Link></li>
              <li><Link to="/list-item" className="text-muted-foreground hover:text-foreground transition-colors">{t("listItem")}</Link></li>
              <li><Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">{t("about")}</Link></li>
              <li><Link to="/help-center" className="text-muted-foreground hover:text-foreground transition-colors">Help Center</Link></li>
              <li><Link to="/fees" className="text-muted-foreground hover:text-foreground transition-colors">{t("fees")}</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4 text-foreground">{t("legal")}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">{t("terms")}</Link></li>
              <li><Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">{t("privacy")}</Link></li>
              <li><Link to="/sms-terms" className="text-muted-foreground hover:text-foreground transition-colors">SMS Terms</Link></li>
              <li><Link to="/safety" className="text-muted-foreground hover:text-foreground transition-colors">Safety</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4 text-foreground">{t("support")}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/support" className="text-muted-foreground hover:text-foreground transition-colors">{t("support")}</Link></li>
              <li><Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">{t("contactUs")}</Link></li>
            </ul>
          </div>
        </div>
        
        {/* Payment Methods Section */}
        <div className="py-6 border-t border-border/50">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Lock className="w-4 h-4" />
              <span>Secure payments powered by</span>
              <span className="font-semibold text-foreground">Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              <PaymentBadge>
                <svg viewBox="0 0 38 24" className="w-8 h-5" aria-label="Visa">
                  <path fill="#1434CB" d="M15.2 17.6h-2.4l1.5-9.2h2.4l-1.5 9.2zM25.4 8.6c-.5-.2-1.2-.4-2.1-.4-2.3 0-4 1.2-4 2.9 0 1.3 1.1 2 2 2.4.9.4 1.2.7 1.2 1.1 0 .6-.7.9-1.4.9-1 0-1.5-.1-2.3-.5l-.3-.2-.3 2c.6.3 1.6.5 2.7.5 2.5 0 4.1-1.2 4.1-3 0-1-.6-1.8-2-2.4-.8-.4-1.3-.7-1.3-1.1 0-.4.4-.8 1.3-.8.8 0 1.3.2 1.7.4l.2.1.4-1.9zM30.3 8.4h-1.8c-.6 0-1 .2-1.2.7l-3.5 8.5h2.5l.5-1.4h3l.3 1.4h2.2l-2-9.2zm-2.9 5.9l1.2-3.4.7 3.4h-1.9zM12.3 8.4L10 14.6l-.2-1.3c-.4-1.4-1.8-3-3.3-3.8l2.1 8h2.5l3.7-9.1h-2.5z"/>
                  <path fill="#F9A533" d="M7.3 8.4H3.5l-.1.2c3 .8 5 2.7 5.8 5l-.8-4.3c-.2-.5-.6-.8-1.1-.9z"/>
                </svg>
              </PaymentBadge>
              <PaymentBadge>
                <svg viewBox="0 0 38 24" className="w-8 h-5" aria-label="Mastercard">
                  <circle fill="#EB001B" cx="15" cy="12" r="7"/>
                  <circle fill="#F79E1B" cx="23" cy="12" r="7"/>
                  <path fill="#FF5F00" d="M19 6.6c1.8 1.4 3 3.5 3 5.9s-1.2 4.5-3 5.9c-1.8-1.4-3-3.5-3-5.9s1.2-4.5 3-5.9z"/>
                </svg>
              </PaymentBadge>
              <PaymentBadge>
                <svg viewBox="0 0 38 24" className="w-8 h-5" aria-label="American Express">
                  <rect fill="#2557D6" width="38" height="24" rx="3"/>
                  <text x="19" y="14" fill="white" fontSize="8" fontWeight="bold" textAnchor="middle">AMEX</text>
                </svg>
              </PaymentBadge>
              <PaymentBadge>
                <svg viewBox="0 0 38 24" className="w-8 h-5" aria-label="Apple Pay">
                  <rect fill="#000" width="38" height="24" rx="3"/>
                  <text x="19" y="14" fill="white" fontSize="6" fontWeight="500" textAnchor="middle">Apple Pay</text>
                </svg>
              </PaymentBadge>
              <PaymentBadge>
                <svg viewBox="0 0 38 24" className="w-8 h-5" aria-label="Google Pay">
                  <rect fill="#fff" width="38" height="24" rx="3"/>
                  <text x="19" y="14" fill="#5F6368" fontSize="5" fontWeight="500" textAnchor="middle">G Pay</text>
                </svg>
              </PaymentBadge>
            </div>
          </div>
        </div>
        
        <div className="pt-6 border-t border-border/50 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Rentify. {t("allRights")}.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
